import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as PlusIcon } from "assets/svgs/Plus.svg";
import FunctionalButton from "components/layout/FunctionalButton";
import {
    type Nav2BehaviorAction,
    type Nav2BehaviorActionType,
    type Nav2BehaviorStage,
    type Nav2BehaviorTreeCatalog,
    type Nav2BehaviorTreeLimits,
    type Nav2BehaviorTreeSettings,
} from "lib/nav2ParamsApi";

const DEFAULT_LIMITS: Nav2BehaviorTreeLimits = {
    replan_rate_hz: { min: 0.1, max: 5.0 },
    navigate_recovery_retries: { min: 0, max: 10 },
    attempts: { min: 1, max: 5 },
    wait_duration_sec: { min: 0.5, max: 60 },
    recovery_distance_m: { min: 0.2, max: 5 },
    backup_distance_m: { min: 0.05, max: 1 },
    backup_speed_mps: { min: 0.05, max: 0.5 },
};

const DEFAULT_CATALOG: Nav2BehaviorTreeCatalog = {
    stage_actions: {
        planning: [
            "clear_global_costmap",
            "wait",
            "retry_planner",
            "alternate_planner",
        ],
        controller: [
            "clear_local_costmap",
            "set_collision_monitor",
            "wait",
            "retry_controller",
            "recovery_controller",
        ],
        recovery: ["clear_local_costmap", "clear_global_costmap", "wait", "backup"],
    },
    planner_ids: ["GridBased", "Navfn"],
    controller_ids: ["FollowPath", "FollowPathRecovery"],
    failure_policies: ["continue", "stop"],
};

type StageName = "planning" | "controller" | "recovery";

const newAction = (type: Nav2BehaviorActionType): Nav2BehaviorAction => {
    const paramsByType: Record<
        Nav2BehaviorActionType,
        Record<string, string | number | boolean>
    > = {
        clear_local_costmap: {},
        clear_global_costmap: {},
        wait: { duration_sec: 5 },
        set_collision_monitor: { enabled: false },
        retry_planner: { attempts: 1 },
        alternate_planner: { planner_id: "Navfn", attempts: 1 },
        retry_controller: { attempts: 1 },
        recovery_controller: {
            controller_id: "FollowPathRecovery",
            distance_m: 1,
            attempts: 1,
        },
        backup: { distance_m: 0.3, speed_mps: 0.15 },
    };
    return {
        id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type,
        on_failure: "continue",
        params: paramsByType[type],
    };
};

const numberValue = (value: string | number | boolean | undefined, fallback: number) =>
    typeof value === "number" ? value : fallback;

const stageHasValidEnding = (name: StageName, stage: Nav2BehaviorStage) => {
    if (!stage.enabled || stage.actions.length === 0 || name === "recovery") {
        return true;
    }
    const terminalTypes =
        name === "planning"
            ? ["retry_planner", "alternate_planner"]
            : ["retry_controller", "recovery_controller"];
    return terminalTypes.includes(stage.actions[stage.actions.length - 1].type);
};

type Props = {
    baseUrl: string | null;
    disabled: boolean;
    /** When true, omit section title (used on the dedicated settings tab). */
    hideChrome?: boolean;
    /** Controlled settings owned by the parent profile manager. */
    settings: Nav2BehaviorTreeSettings | null;
    onSettingsChange: (next: Nav2BehaviorTreeSettings) => void;
    dirty: boolean;
    limits: Nav2BehaviorTreeLimits;
    catalog: Nav2BehaviorTreeCatalog;
    loading?: boolean;
    /** Reload selected profile / catalog from parent. */
    onRefresh: () => void;
    /** True when any stage flow is invalid (exposed for parent Save gating). */
    onInvalidChange?: (invalid: boolean) => void;
};

type StageEditorProps = {
    name: StageName;
    stage: Nav2BehaviorStage;
    catalog: Nav2BehaviorTreeCatalog;
    limits: Nav2BehaviorTreeLimits;
    disabled: boolean;
    embedded?: boolean;
    onChange: (stage: Nav2BehaviorStage) => void;
};

const STAGE_TABS: StageName[] = ["planning", "controller", "recovery"];

const INPUT_CLASS =
    "min-h-[44px] rounded-r5 border border-black60 bg-black90 px-3 py-2 text-h5 text-white outline-none focus:border-primary2 disabled:opacity-50";

const TAB_BUTTON_CLASS =
    "min-h-[44px] flex-1 rounded-r3 px-3 py-2 text-h5 sm:min-w-[7rem]";

const CARD_CLASS =
    "rounded-r5 border-l-4 border-y border-r border-black70 bg-black80 p-3";

const STAGE_BORDER_COLORS: Record<StageName, string> = {
    planning: "border-l-cyan-400",
    controller: "border-l-amber-400",
    recovery: "border-l-rose-400",
};

const StageEditor: React.FC<StageEditorProps> = ({
    name,
    stage,
    catalog,
    limits,
    disabled,
    embedded = false,
    onChange,
}) => {
    const { t } = useTranslation("settings");
    const availableActions = catalog.stage_actions[name];
    const [addType, setAddType] = useState<Nav2BehaviorActionType>(
        availableActions[0] ?? "wait"
    );

    useEffect(() => {
        if (!availableActions.includes(addType)) {
            setAddType(availableActions[0] ?? "wait");
        }
    }, [addType, availableActions]);

    const updateAction = (index: number, next: Nav2BehaviorAction) => {
        const actions = [...stage.actions];
        actions[index] = next;
        onChange({ ...stage, actions });
    };

    const updateParam = (
        index: number,
        key: string,
        value: string | number | boolean
    ) => {
        const action = stage.actions[index];
        updateAction(index, {
            ...action,
            params: { ...action.params, [key]: value },
        });
    };

    const moveAction = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= stage.actions.length) return;
        const actions = [...stage.actions];
        [actions[index], actions[target]] = [actions[target], actions[index]];
        onChange({ ...stage, actions });
    };

    const bounds = (key: string) => limits[key] ?? { min: 0, max: 100 };
    const validEnding = stageHasValidEnding(name, stage);

    return (
        <section className={embedded ? "pt-1" : "rounded-r5 border border-black70 bg-black90 p-4"}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className={embedded ? "min-w-0 flex-1" : undefined}>
                    {!embedded ? (
                        <h4 className="text-h5 font-semibold text-white">
                            {t(`navigation.behaviorTree.stages.${name}.title`)}
                        </h4>
                    ) : null}
                    <p
                        className={
                            embedded
                                ? "text-h5 text-black40"
                                : "mt-1 text-h5 text-black40"
                        }
                    >
                        {t(`navigation.behaviorTree.stages.${name}.description`)}
                    </p>
                </div>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-r5 text-h5 text-white">
                    <input
                        type="checkbox"
                        checked={stage.enabled}
                        disabled={disabled}
                        onChange={(event) =>
                            onChange({ ...stage, enabled: event.target.checked })
                        }
                    />
                    <span>{t("navigation.behaviorTree.enableFallback")}</span>
                </label>
            </div>

            {name !== "recovery" ? (
                <label className="mt-5 flex flex-col gap-2">
                    <span className="text-h5 font-semibold text-white">
                        {t(
                            name === "planning"
                                ? "navigation.behaviorTree.primaryPlanner"
                                : "navigation.behaviorTree.primaryController"
                        )}
                    </span>
                    <select
                        className={INPUT_CLASS}
                        value={stage.primary_id ?? ""}
                        disabled={disabled}
                        onChange={(event) =>
                            onChange({ ...stage, primary_id: event.target.value })
                        }
                    >
                        {(name === "planning"
                            ? catalog.planner_ids
                            : catalog.controller_ids
                        ).map((id) => (
                            <option key={id} value={id}>
                                {id}
                            </option>
                        ))}
                    </select>
                </label>
            ) : null}

            <div className="mt-5 flex flex-col gap-3">
                {stage.actions.length === 0 ? (
                    <div className={CARD_CLASS}>
                        <p className="text-h5 text-black40">
                            {t("navigation.behaviorTree.noFallbackActions")}
                        </p>
                    </div>
                ) : null}

                {stage.actions.map((action, index) => {
                    const attempts = bounds("attempts");
                    const wait = bounds("wait_duration_sec");
                    const recoveryDistance = bounds("recovery_distance_m");
                    const backupDistance = bounds("backup_distance_m");
                    const backupSpeed = bounds("backup_speed_mps");
                    return (
                        <article
                            key={action.id}
                            className={`${CARD_CLASS} ${STAGE_BORDER_COLORS[name]}`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black70 text-h5 font-semibold text-primary2">
                                        {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="truncate text-h5 font-semibold text-white">
                                            {t(
                                                `navigation.behaviorTree.actions.${action.type}`
                                            )}
                                        </p>
                                        <p className="text-h5 text-black50">
                                            {t(
                                                "navigation.behaviorTree.whenPreviousFails"
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                    <button
                                        type="button"
                                        className="min-h-[44px] min-w-[44px] rounded-r5 border border-black60 text-white transition-colors hover:bg-black70 disabled:opacity-30"
                                        disabled={disabled || index === 0}
                                        aria-label={t(
                                            "navigation.behaviorTree.moveActionUp"
                                        )}
                                        onClick={() => moveAction(index, -1)}
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        className="min-h-[44px] min-w-[44px] rounded-r5 border border-black60 text-white transition-colors hover:bg-black70 disabled:opacity-30"
                                        disabled={
                                            disabled || index === stage.actions.length - 1
                                        }
                                        aria-label={t(
                                            "navigation.behaviorTree.moveActionDown"
                                        )}
                                        onClick={() => moveAction(index, 1)}
                                    >
                                        ↓
                                    </button>
                                    <button
                                        type="button"
                                        className="min-h-[44px] rounded-r5 border border-red-500/60 px-3 text-h5 text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-30"
                                        disabled={disabled}
                                        onClick={() =>
                                            onChange({
                                                ...stage,
                                                actions: stage.actions.filter(
                                                    (_, actionIndex) =>
                                                        actionIndex !== index
                                                ),
                                            })
                                        }
                                    >
                                        {t("navigation.behaviorTree.removeAction")}
                                    </button>
                                </div>
                            </div>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                {action.type === "wait" ? (
                                    <label className="flex flex-col gap-1">
                                        <span className="text-h5 text-black40">
                                            {t(
                                                "navigation.behaviorTree.fields.duration"
                                            )}
                                        </span>
                                        <input
                                            className={INPUT_CLASS}
                                            type="number"
                                            min={wait.min}
                                            max={wait.max}
                                            step={0.5}
                                            value={numberValue(
                                                action.params.duration_sec,
                                                5
                                            )}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateParam(
                                                    index,
                                                    "duration_sec",
                                                    Number(event.target.value)
                                                )
                                            }
                                        />
                                    </label>
                                ) : null}

                                {[
                                    "retry_planner",
                                    "alternate_planner",
                                    "retry_controller",
                                    "recovery_controller",
                                ].includes(action.type) ? (
                                    <label className="flex flex-col gap-1">
                                        <span className="text-h5 text-black40">
                                            {t(
                                                "navigation.behaviorTree.fields.attempts"
                                            )}
                                        </span>
                                        <input
                                            className={INPUT_CLASS}
                                            type="number"
                                            min={attempts.min}
                                            max={attempts.max}
                                            step={1}
                                            value={numberValue(
                                                action.params.attempts,
                                                1
                                            )}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateParam(
                                                    index,
                                                    "attempts",
                                                    Number(event.target.value)
                                                )
                                            }
                                        />
                                    </label>
                                ) : null}

                                {action.type === "alternate_planner" ? (
                                    <label className="flex flex-col gap-1">
                                        <span className="text-h5 text-black40">
                                            {t(
                                                "navigation.behaviorTree.fields.planner"
                                            )}
                                        </span>
                                        <select
                                            className={INPUT_CLASS}
                                            value={String(
                                                action.params.planner_id ?? "Navfn"
                                            )}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateParam(
                                                    index,
                                                    "planner_id",
                                                    event.target.value
                                                )
                                            }
                                        >
                                            {catalog.planner_ids.map((id) => (
                                                <option key={id} value={id}>
                                                    {id}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : null}

                                {action.type === "recovery_controller" ? (
                                    <>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-h5 text-black40">
                                                {t(
                                                    "navigation.behaviorTree.fields.controller"
                                                )}
                                            </span>
                                            <select
                                                className={INPUT_CLASS}
                                                value={String(
                                                    action.params.controller_id ??
                                                        "FollowPathRecovery"
                                                )}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    updateParam(
                                                        index,
                                                        "controller_id",
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                {catalog.controller_ids.map((id) => (
                                                    <option key={id} value={id}>
                                                        {id}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-h5 text-black40">
                                                {t(
                                                    "navigation.behaviorTree.fields.progressDistance"
                                                )}
                                            </span>
                                            <input
                                                className={INPUT_CLASS}
                                                type="number"
                                                min={recoveryDistance.min}
                                                max={recoveryDistance.max}
                                                step={0.1}
                                                value={numberValue(
                                                    action.params.distance_m,
                                                    1
                                                )}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    updateParam(
                                                        index,
                                                        "distance_m",
                                                        Number(event.target.value)
                                                    )
                                                }
                                            />
                                        </label>
                                    </>
                                ) : null}

                                {action.type === "backup" ? (
                                    <>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-h5 text-black40">
                                                {t(
                                                    "navigation.behaviorTree.fields.backupDistance"
                                                )}
                                            </span>
                                            <input
                                                className={INPUT_CLASS}
                                                type="number"
                                                min={backupDistance.min}
                                                max={backupDistance.max}
                                                step={0.05}
                                                value={numberValue(
                                                    action.params.distance_m,
                                                    0.3
                                                )}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    updateParam(
                                                        index,
                                                        "distance_m",
                                                        Number(event.target.value)
                                                    )
                                                }
                                            />
                                        </label>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-h5 text-black40">
                                                {t(
                                                    "navigation.behaviorTree.fields.backupSpeed"
                                                )}
                                            </span>
                                            <input
                                                className={INPUT_CLASS}
                                                type="number"
                                                min={backupSpeed.min}
                                                max={backupSpeed.max}
                                                step={0.05}
                                                value={numberValue(
                                                    action.params.speed_mps,
                                                    0.15
                                                )}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    updateParam(
                                                        index,
                                                        "speed_mps",
                                                        Number(event.target.value)
                                                    )
                                                }
                                            />
                                        </label>
                                    </>
                                ) : null}

                                {action.type === "set_collision_monitor" ? (
                                    <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-h5 text-white">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(
                                                action.params.enabled
                                            )}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateParam(
                                                    index,
                                                    "enabled",
                                                    event.target.checked
                                                )
                                            }
                                        />
                                        {t(
                                            "navigation.behaviorTree.fields.collisionMonitorEnabled"
                                        )}
                                    </label>
                                ) : null}

                                <label className="flex flex-col gap-1">
                                    <span className="text-h5 text-black40">
                                        {t(
                                            "navigation.behaviorTree.fields.ifActionFails"
                                        )}
                                    </span>
                                    <select
                                        className={INPUT_CLASS}
                                        value={action.on_failure}
                                        disabled={disabled}
                                        onChange={(event) =>
                                            updateAction(index, {
                                                ...action,
                                                on_failure: event.target.value as
                                                    | "continue"
                                                    | "stop",
                                            })
                                        }
                                    >
                                        <option value="continue">
                                            {t(
                                                "navigation.behaviorTree.continueFallback"
                                            )}
                                        </option>
                                        <option value="stop">
                                            {t(
                                                "navigation.behaviorTree.stopBranch"
                                            )}
                                        </option>
                                    </select>
                                </label>
                            </div>
                        </article>
                    );
                })}
                {!validEnding ? (
                    <div className={`${CARD_CLASS} ${STAGE_BORDER_COLORS[name]}`}>
                        <p className="text-h5 text-red-400">
                            {t(`navigation.behaviorTree.stages.${name}.invalidEnding`)}
                        </p>
                    </div>
                ) : null}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <select
                    className={`${INPUT_CLASS} min-w-0 flex-1`}
                    value={addType}
                    disabled={disabled}
                    onChange={(event) =>
                        setAddType(event.target.value as Nav2BehaviorActionType)
                    }
                    aria-label={t("navigation.behaviorTree.actionToAdd")}
                >
                    {availableActions.map((type) => (
                        <option key={type} value={type}>
                            {t(`navigation.behaviorTree.actions.${type}`)}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-r5 border border-primary2 px-4 text-h5 font-semibold text-primary2 transition-colors hover:bg-primary2/10 disabled:opacity-50"
                    disabled={disabled || stage.actions.length >= 12}
                    onClick={() =>
                        onChange({
                            ...stage,
                            actions: [...stage.actions, newAction(addType)],
                        })
                    }
                >
                    <PlusIcon className="h-4 w-4" aria-hidden />
                    {t("navigation.behaviorTree.addAction")}
                </button>
            </div>
        </section>
    );
};

export { stageHasValidEnding, STAGE_TABS, DEFAULT_LIMITS, DEFAULT_CATALOG };
export type { StageName };

const NavigationBehaviorTreeSection: React.FC<Props> = ({
    baseUrl,
    disabled,
    hideChrome = false,
    settings,
    onSettingsChange,
    dirty,
    limits,
    catalog,
    loading = false,
    onRefresh,
    onInvalidChange,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const [activeStage, setActiveStage] = useState<StageName>("planning");

    const changeSettings = (next: Nav2BehaviorTreeSettings) => {
        onSettingsChange(next);
    };

    const controlsDisabled = disabled || !baseUrl || loading || !settings;
    const sectionTitle = t("navigation.behaviorTree.title", {
        defaultValue: "Behavior tree",
    });

    const hasInvalidFlow = useMemo(
        () =>
            settings
                ? STAGE_TABS.some((name) => !stageHasValidEnding(name, settings[name]))
                : false,
        [settings]
    );

    useEffect(() => {
        onInvalidChange?.(hasInvalidFlow);
    }, [hasInvalidFlow, onInvalidChange]);

    if (!baseUrl) {
        return null;
    }

    if (!settings) {
        return (
            <div
                className={
                    hideChrome
                        ? "rounded-r10 border border-black80 bg-black90 p-4"
                        : "mb-6 rounded-r5 border border-black80 bg-black80 p-4"
                }
            >
                <p className="text-h5 text-black40">
                    {loading
                        ? t("navigation.behaviorTree.loading")
                        : t("navigation.behaviorTree.unavailable", {
                              defaultValue:
                                  "Behavior tree controls are not available on this ARC. Update nav_config_api to enable them.",
                          })}
                </p>
            </div>
        );
    }

    return (
        <div
            className={
                hideChrome
                    ? "rounded-r10 border border-black80 bg-black90 p-4"
                    : "mb-6 rounded-r5 border border-black80 bg-black80 p-3"
            }
        >
            {!hideChrome ? (
                <div>
                    <p className="text-h5 font-semibold text-white">{sectionTitle}</p>
                    <p className="mt-1 text-h5 text-black40">
                        {t("navigation.behaviorTree.description", {
                            defaultValue:
                                "Choose what happens when planning, path following, or recovery fails. Actions run from top to bottom.",
                        })}
                    </p>
                    <p className="mt-1 text-h5 text-black60">
                        {t("navigation.behaviorTree.restartHint", {
                            defaultValue:
                                "These changes require a navigation restart to take effect.",
                        })}
                    </p>
                </div>
            ) : null}

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-2">
                    <span className="text-h5 font-semibold text-white">
                        {t("navigation.behaviorTree.replanRate")}
                    </span>
                    <span className="text-h5 text-black50">
                        {t("navigation.behaviorTree.replanRateHint")}
                    </span>
                    <input
                        className={INPUT_CLASS}
                        type="number"
                        min={limits.replan_rate_hz?.min}
                        max={limits.replan_rate_hz?.max}
                        step={0.1}
                        value={settings.replan_rate_hz}
                        disabled={controlsDisabled}
                        onChange={(event) =>
                            changeSettings({
                                ...settings,
                                replan_rate_hz: Number(event.target.value),
                            })
                        }
                    />
                </label>
                <label className="flex flex-col gap-2">
                    <span className="text-h5 font-semibold text-white">
                        {t("navigation.behaviorTree.navigateRetries")}
                    </span>
                    <span className="text-h5 text-black50">
                        {t("navigation.behaviorTree.navigateRetriesHint")}
                    </span>
                    <input
                        className={INPUT_CLASS}
                        type="number"
                        min={limits.navigate_recovery_retries?.min}
                        max={limits.navigate_recovery_retries?.max}
                        step={1}
                        value={settings.navigate_recovery_retries}
                        disabled={controlsDisabled}
                        onChange={(event) =>
                            changeSettings({
                                ...settings,
                                navigate_recovery_retries: Number(
                                    event.target.value
                                ),
                            })
                        }
                    />
                </label>
            </div>

            <div className="mt-5 rounded-r10 border border-black80 bg-black90 p-4">
                <div
                    className="flex w-full flex-wrap gap-1 rounded-r5 border border-black80 bg-black70 p-1"
                    role="tablist"
                    aria-label={t("navigation.behaviorTree.tabsAria", {
                        defaultValue: "Failure handling stages",
                    })}
                >
                    {STAGE_TABS.map((name) => {
                        const invalid = !stageHasValidEnding(name, settings[name]);
                        const selected = activeStage === name;
                        return (
                            <button
                                key={name}
                                type="button"
                                role="tab"
                                id={`nav-bt-tab-${name}`}
                                aria-selected={selected}
                                aria-controls={`nav-bt-panel-${name}`}
                                disabled={controlsDisabled}
                                onClick={() => setActiveStage(name)}
                                className={`${TAB_BUTTON_CLASS} ${
                                    selected
                                        ? "bg-black80 font-semibold text-white"
                                        : "text-black40 hover:bg-black80/60 hover:text-white"
                                } disabled:cursor-not-allowed disabled:opacity-50`}
                            >
                                <span className="inline-flex items-center justify-center gap-1.5">
                                    {t(`navigation.behaviorTree.tabs.${name}`, {
                                        defaultValue: t(
                                            `navigation.behaviorTree.stages.${name}.title`
                                        ),
                                    })}
                                    {invalid ? (
                                        <span
                                            className="h-2 w-2 shrink-0 rounded-full bg-red-400"
                                            aria-hidden
                                        />
                                    ) : null}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div
                    id={`nav-bt-panel-${activeStage}`}
                    role="tabpanel"
                    aria-labelledby={`nav-bt-tab-${activeStage}`}
                    className="mt-4"
                >
                    <StageEditor
                        embedded
                        name={activeStage}
                        stage={settings[activeStage]}
                        catalog={catalog}
                        limits={limits}
                        disabled={controlsDisabled}
                        onChange={(stage) =>
                            changeSettings({ ...settings, [activeStage]: stage })
                        }
                    />
                </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-black70 pt-4">
                <div className="min-w-0">
                    {hasInvalidFlow ? (
                        <p className="text-h5 text-red-400">
                            {t("navigation.behaviorTree.cannotSave", {
                                defaultValue:
                                    "Fix the highlighted stage before saving.",
                            })}
                        </p>
                    ) : null}
                    {dirty && !hasInvalidFlow ? (
                        <p className="text-h5 text-black40">
                            {t("navigation.behaviorTree.unsaved", {
                                defaultValue: "Unsaved changes.",
                            })}
                        </p>
                    ) : null}
                </div>
                <FunctionalButton
                    title={t("common:refresh", { defaultValue: "Refresh" })}
                    type="button"
                    className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                    disabled={!baseUrl || loading}
                    onClick={() => onRefresh()}
                />
            </div>
        </div>
    );
};

export default NavigationBehaviorTreeSection;
